class QuoteRequestFormModal extends HTMLElement {
    constructor() {
        super();
        this.modalOuter = this.querySelector('.quote-request-form__outer');
        this.openModal = this.openModal.bind(this);
        this.handleClicks = this.handleClicks.bind(this);
        this.handleSqFtChange = this.handleSqFtChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    connectedCallback() {
        document.addEventListener('quote:modal:open', this.openModal);
        document.addEventListener('sqft:change', this.handleSqFtChange);
        this.addEventListener('click', this.handleClicks);
        this.addEventListener('submit', this.handleSubmit);

        // On the post-submit reload, send to Klaviyo only if this form actually posted.
        this.maybeFireKlaviyoFromStorage();
    }

    disconnectedCallback() {
        // The trigger moves this element to <body> on init, which disconnects and
        // reconnects it. Removing listeners here keeps each bound exactly once.
        document.removeEventListener('quote:modal:open', this.openModal);
        document.removeEventListener('sqft:change', this.handleSqFtChange);
        this.removeEventListener('click', this.handleClicks);
        this.removeEventListener('submit', this.handleSubmit);
    }

    openModal() {
        this.modalOuter.classList.add('is-active');
        document.body.classList.add('no-scroll');
    }

    handleClicks(event) {
        const outer = event.target.closest('.quote-request-form__outer');
        const inner = event.target.closest('.quote-request-form__inner');
        const closer = event.target.closest('.modal-closer');

        if (outer && !inner || closer) {
            this.modalOuter.classList.remove('is-active');
            document.body.classList.remove('no-scroll');
        }
    }

    handleSqFtChange(event) {
        const sqFtFormInput = this.querySelector('#ContactForm-SqFt');
        const totalSqFt = event.detail.totalSqFt;

        if (!sqFtFormInput || !totalSqFt) return;

        sqFtFormInput.value = totalSqFt.toFixed(2);
    }

    // ---- Klaviyo capture --------------------------------------------------
    // Why two phases: Shopify wraps the contact form in reCAPTCHA, and the
    // native POST reloads the page. To avoid sending bots / failed-captcha /
    // abandoned submissions to Klaviyo, we stash the field values on submit and
    // only send to Klaviyo on the success reload, gated by a server-rendered
    // marker that proves THIS form posted successfully.

    // 1. On submit: stash the values. We do NOT preventDefault — the native
    //    Shopify POST (and its reCAPTCHA + store email) proceeds as normal.
    handleSubmit() {
        try {
            if (this.dataset.klaviyoEnable === 'false') return;

            const companyId = (this.dataset.klaviyoCompanyId || '').trim();
            if (!companyId) return;

            const data = this.collectFormData();
            if (!data.email) return;

            sessionStorage.setItem(
                QuoteRequestFormModal.STORAGE_KEY,
                JSON.stringify({ data: data, savedAt: Date.now() })
            );
        } catch (error) {
            // Never interfere with the native submit.
            console.warn('Quote form capture (stash) skipped:', error);
        }
    }

    // 2. On the success reload: if this form posted and we have a stash, send it.
    maybeFireKlaviyoFromStorage() {
        try {
            if (this.dataset.klaviyoEnable === 'false') return;

            const companyId = (this.dataset.klaviyoCompanyId || '').trim();
            if (!companyId) return;

            // Server-rendered only when THIS form's form.posted_successfully? is true.
            if (!this.querySelector('[data-quote-posted]')) return;

            const raw = sessionStorage.getItem(QuoteRequestFormModal.STORAGE_KEY);
            if (!raw) return;
            sessionStorage.removeItem(QuoteRequestFormModal.STORAGE_KEY);

            const parsed = JSON.parse(raw);
            const data = parsed && parsed.data;
            if (!data || !data.email) return;

            // Ignore a stale stash (e.g. submitted, abandoned, returned much later).
            if (parsed.savedAt && Date.now() - parsed.savedAt > QuoteRequestFormModal.STORAGE_TTL_MS) {
                return;
            }

            this.whenKlaviyoReady(() => {
                this.identifyProfile(data);
                this.trackQuoteEvent(data);
                if (data.acceptsMarketing) {
                    this.subscribeToList(companyId, data);
                }
            });
        } catch (error) {
            console.warn('Quote form capture (send) skipped:', error);
        }
    }

    // The Klaviyo onsite script loads async; wait briefly for it on the reload.
    whenKlaviyoReady(callback, attempts) {
        attempts = attempts || 0;
        if (window.klaviyo) {
            callback();
            return;
        }
        if (attempts > 50) return; // give up after ~5s
        setTimeout(() => this.whenKlaviyoReady(callback, attempts + 1), 100);
    }

    collectFormData() {
        const getValue = (id) => {
            const el = this.querySelector('#' + id);
            return el ? el.value.trim() : '';
        };
        const getCheckedValues = (selector) =>
            Array.from(this.querySelectorAll(selector)).map((el) => el.value);

        const productEl = this.querySelector('input[name="contact[Product]"]');
        const projectTypeEl = this.querySelector('input[name="contact[Project Type]"]:checked');
        const acceptsEl = this.querySelector('#ContactForm-AcceptsMarketing');

        return {
            firstName: getValue('ContactForm-FirstName'),
            lastName: getValue('ContactForm-LastName'),
            email: getValue('ContactForm-email'),
            phone: getValue('ContactForm-Telephone'),
            country: getValue('ContactForm-Country'),
            street1: getValue('ContactForm-Street1'),
            street2: getValue('ContactForm-Street2'),
            city: getValue('ContactForm-City'),
            state: getValue('ContactForm-State'),
            zip: getValue('ContactForm-Zip'),
            product: productEl ? productEl.value.trim() : '',
            projectType: projectTypeEl ? projectTypeEl.value : '',
            occupations: getCheckedValues('input[name^="contact[Occupation"]:checked'),
            totalSqFt: getValue('ContactForm-SqFt'),
            widths: getCheckedValues('input[name^="contact[Width"]:checked'),
            acceptsMarketing: acceptsEl ? acceptsEl.checked : false,
        };
    }

    identifyProfile(data) {
        const properties = this.compact({
            $email: data.email,
            $first_name: data.firstName,
            $last_name: data.lastName,
            $phone_number: data.phone,
            $country: data.country,
            $region: data.state,
            $city: data.city,
            $zip: data.zip,
            $address1: data.street1,
            $address2: data.street2,
            'Accepts Marketing': data.acceptsMarketing,
        });

        this.klaviyoCall('identify', properties);
    }

    trackQuoteEvent(data) {
        const properties = this.compact({
            Product: data.product,
            'Project Type': data.projectType,
            Occupation: data.occupations,
            'Total Square Feet': data.totalSqFt,
            Widths: data.widths,
            Country: data.country,
            State: data.state,
        });

        this.klaviyoCall('track', 'Requested Quote', properties);
    }

    subscribeToList(companyId, data) {
        const listId = (this.dataset.klaviyoListId || '').trim();
        if (!listId) return;

        const body = {
            data: {
                type: 'subscription',
                attributes: {
                    custom_source: 'Quote Request Form',
                    profile: {
                        data: {
                            type: 'profile',
                            attributes: {
                                email: data.email,
                                subscriptions: {
                                    email: { marketing: { consent: 'SUBSCRIBED' } },
                                },
                            },
                        },
                    },
                },
                relationships: {
                    list: { data: { type: 'list', id: listId } },
                },
            },
        };

        // `revision` is Klaviyo's API version. The `subscriptions` object under
        // profile attributes requires a recent revision (older ones 400 with
        // "'subscriptions' is not a valid field for the resource 'profile'").
        fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                revision: '2026-04-15',
            },
            body: JSON.stringify(body),
        }).catch(() => {});
    }

    // Calls the Klaviyo onsite object whether it exposes the modern method API
    // (klaviyo.identify(...)) or the legacy array-queue API (klaviyo.push([...])).
    klaviyoCall(method, ...args) {
        const klaviyo = window.klaviyo;
        if (!klaviyo) return;

        if (typeof klaviyo[method] === 'function') {
            Promise.resolve(klaviyo[method](...args)).catch(() => {});
        } else if (typeof klaviyo.push === 'function') {
            klaviyo.push([method, ...args]);
        }
    }

    // Drops empty strings/null and empty arrays; keeps booleans (incl. false).
    compact(obj) {
        const result = {};
        Object.keys(obj).forEach((key) => {
            const value = obj[key];
            if (Array.isArray(value)) {
                if (value.length) result[key] = value;
            } else if (typeof value === 'boolean') {
                result[key] = value;
            } else if (value !== '' && value != null) {
                result[key] = value;
            }
        });
        return result;
    }
}

QuoteRequestFormModal.STORAGE_KEY = 'quoteRequestKlaviyo';
QuoteRequestFormModal.STORAGE_TTL_MS = 30 * 60 * 1000; // 30 minutes

customElements.define('quote-request-form-modal', QuoteRequestFormModal);
